'use client';

import { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Error boundary component to catch rendering errors and show fallback UI.
 * Prevents component failures from crashing the entire page.
 * Reports errors to /api/log-error for D1 persistence.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Component error:', error, info);

    // Report to D1
    try {
      const payload = JSON.stringify({
        errorType: 'js_error',
        message: `[React] ${error.message}`.substring(0, 500),
        stackTrace: (error.stack || '').substring(0, 2000),
        pageUrl: typeof window !== 'undefined' ? window.location.pathname : '',
      });

      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon('/api/log-error', new Blob([payload], { type: 'application/json' }));
      } else if (typeof fetch !== 'undefined') {
        fetch('/api/log-error', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      // Never throw from error reporting
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
